import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link, useParams } from "react-router-dom";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS, API_BASE_URL } from "../../constants/api-urls";
import "./BidSuccess.scss";

const API_BASE_URL_MEDIA = API_BASE_URL.replace("/api/v1", "");

export default function BidSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auctionId } = useParams();
  
  // Get data from location state
  const { auction, bidAmount, bidId } = location.state || {};
  
  const [loading, setLoading] = useState(true);
  const [auctionData, setAuctionData] = useState(auction || null);
  const [bidderName, setBidderName] = useState("");
  const [currentBidAmount, setCurrentBidAmount] = useState(bidAmount || null);

  useEffect(() => {
    // Get bidder name from current user
    const user = StorageService.getUser();
    if (user) {
      setBidderName(user.company_name || user.name || user.username || "You");
    }

    // If auction data not passed, fetch it using auctionId from URL or state
    const idToFetch = auctionId || location.state?.auctionId;
    if (!auction && !auctionData && idToFetch) {
      fetchAuction(idToFetch);
    } else if (auction || auctionData) {
      setLoading(false);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionId]);

  const fetchAuction = async (id) => {
    try {
      setLoading(true);
      const response = await CommonService.get(`${API_URLS.AUCTION.BASE}/${id}`);
      
      if (response && response.success && response.data) {
        setAuctionData(response.data);
        
        // If bidAmount not in state, try to get from latest bid
        if (!bidAmount && !currentBidAmount) {
          // Fetch bids to get the latest bid amount
          try {
            const bidsResponse = await CommonService.get(API_URLS.AUCTION.BIDS(id));
            if (bidsResponse && bidsResponse.success && bidsResponse.data) {
              const bids = Array.isArray(bidsResponse.data) ? bidsResponse.data : [];
              const highestBid = bids.find(b => b.is_highest_bid === 'Y');
              if (highestBid) {
                setCurrentBidAmount(highestBid.bid_amount);
              } else if (bids.length > 0) {
                // Use the most recent bid
                const sortedBids = bids.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                setCurrentBidAmount(sortedBids[0].bid_amount);
              }
            }
          } catch (bidError) {
            console.error("Error fetching bids:", bidError);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching auction:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get full image/video URL from access path
  const getMediaUrl = (accessPath) => {
    if (!accessPath) return '';
    
    // If already a full URL, return as is
    if (accessPath.startsWith('http://') || accessPath.startsWith('https://')) {
      return accessPath;
    }
    
    const baseUrl = API_BASE_URL_MEDIA;
    
    // Handle old path formats that might have Windows absolute paths
    if (accessPath.includes('C:/usr/local/b2b/seller/AuctionProduct') || accessPath.includes('C:\\usr\\local\\b2b\\seller\\AuctionProduct')) {
      // Extract auction ID and filename from path format
      const match = accessPath.match(/AuctionProduct[\/\\](\d+)[\/\\]([^\/\\]+)$/);
      if (match) {
        const extractedAuctionId = match[1];
        const fileName = match[2];
        return `${baseUrl}/auction-media/${extractedAuctionId}/${fileName}`;
      }
      // Handle temp folder
      const tempMatch = accessPath.match(/AuctionProduct[\/\\]temp[\/\\]([^\/\\]+)$/);
      if (tempMatch) {
        const fileName = tempMatch[1];
        return `${baseUrl}/auction-media/temp/${fileName}`;
      }
    }
    
    // If it's already a relative path starting with /auction-media, use it directly
    if (accessPath.startsWith('/auction-media/')) {
      return `${baseUrl}${accessPath}`;
    }
    
    // If relative path, prepend base URL
    let cleanPath = accessPath;
    if (cleanPath.startsWith(':3000')) {
      cleanPath = cleanPath.replace(/^:3000/, '');
    }
    if (!cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }
    
    // If path doesn't start with /auction-media, assume it's for the current auction
    if (!cleanPath.startsWith('/auction-media')) {
      // Try to extract filename from path
      const fileName = cleanPath.split('/').pop();
      if (fileName && auctionData?.auction_id) {
        return `${baseUrl}/auction-media/${auctionData.auction_id}/${fileName}`;
      }
    }
    
    return `${baseUrl}${cleanPath}`;
  };

  const formatCurrency = (amount) => {
    // Handle NaN, null, undefined, or invalid numbers
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const getCategoryName = () => {
    if (!auctionData) return "N/A";
    return auctionData.category_name || "N/A";
  };

  const getCondition = () => {
    if (!auctionData) return "N/A";
    // Try to get condition from product_attributes
    if (auctionData.product_attributes) {
      const attrs = typeof auctionData.product_attributes === 'string' 
        ? JSON.parse(auctionData.product_attributes) 
        : auctionData.product_attributes;
      
      if (attrs.condition_grade) return attrs.condition_grade;
      if (attrs.condition) return attrs.condition;
    }
    return "Excellent"; // Default
  };

  const getYear = () => {
    if (!auctionData) return "N/A";
    // Try to get year from product_attributes
    if (auctionData.product_attributes) {
      const attrs = typeof auctionData.product_attributes === 'string' 
        ? JSON.parse(auctionData.product_attributes) 
        : auctionData.product_attributes;
      
      if (attrs.year_of_manufacture) return attrs.year_of_manufacture;
      if (attrs.year) return attrs.year;
    }
    return "N/A";
  };

  const getLocation = () => {
    if (!auctionData) return "N/A";
    const locationParts = [
      auctionData.country,
      auctionData.city,
      auctionData.state
    ].filter(Boolean);
    return locationParts.length > 0 ? locationParts.join(", ") : "India";
  };

  if (loading) {
    return <div className="bid-success-page"><div className="container">Loading...</div></div>;
  }

  if (!auctionData) {
    return (
      <div className="bid-success-page">
        <div className="container">
          <div className="error-message">Auction data not found. Please go back and try again.</div>
          <button className="btn-primary" onClick={() => navigate("/auction")}>
            Back to Auctions
          </button>
        </div>
      </div>
    );
  }

  const images = auctionData.images && Array.isArray(auctionData.images) ? auctionData.images : [];
  const mainImage = images.length > 0 ? images[0] : null;
  const displayBidAmount = currentBidAmount || bidAmount || auctionData.reserve_price || 0;
  const reservePrice = auctionData.reserve_price || 0;
  const minIncrement = auctionData.bid_increment || 0;

  return (
    <div className="bid-success-page">
      <div className="container">
        {/* Header Section */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
              </svg>
            </div>
            <div className="header-text">
              <h1>Step 1: Buyer Places Bid</h1>
              <p className="subtitle">Active bidding on live auction</p>
            </div>
          </div>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="success-content">
          {/* Left Column - Item Details Card */}
          <div className="item-details-card">
            <div className="item-image">
              {mainImage ? (
                <img
                  src={getMediaUrl(mainImage)}
                  alt={auctionData.product_name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={`placeholder-image ${mainImage ? 'hidden' : ''}`}>
                No Image Available
              </div>
            </div>
            <div className="item-info">
              <h2 className="item-title">{auctionData.product_name}</h2>
              <div className="item-specs">
                <div className="spec-item">
                  <span className="spec-label">Category:</span>
                  <span className="spec-value">{getCategoryName()}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Condition:</span>
                  <span className="spec-value">{getCondition()}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Year:</span>
                  <span className="spec-value">{getYear()}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Location:</span>
                  <span className="spec-value">{getLocation()}</span>
                </div>
              </div>
              <div className="item-description">
                <p>{auctionData.product_description || "No description available."}</p>
              </div>
            </div>
          </div>

          {/* Right Column - Bid Details Card */}
          <div className="bid-details-card">
            <div className="bid-header">
              <span className="bid-label">Current Highest Bid</span>
            </div>
            <div className="bid-amount-large">
              {formatCurrency(displayBidAmount)}
            </div>
            <div className="bidder-name">
              by {bidderName}
            </div>
            <div className="bid-info-list">
              <div className="bid-info-item">
                <span className="info-label">Reserve Price:</span>
                <span className="info-value">{formatCurrency(reservePrice)}</span>
              </div>
              <div className="bid-info-item">
                <span className="info-label">Min Increment:</span>
                <span className="info-value">{formatCurrency(minIncrement)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Success Banner */}
        <div className="success-banner">
          <div className="success-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5"></path>
            </svg>
          </div>
          <div className="success-content-text">
            <h3>Bid Placed Successfully!</h3>
            <p>
              Your bid of <span className="highlight-amount">{formatCurrency(displayBidAmount)}</span> is currently the highest bid. You will be notified if another bidder outbids you.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="btn-secondary" onClick={() => navigate("/auction")}>
            Browse More Auctions
          </button>
          <Link to={`/auction/${auctionData.auction_id}`} className="btn-primary">
            View Auction Details
          </Link>
        </div>
      </div>
    </div>
  );
}
